import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useResponsive from '../utils/useResponsive';
import { formatearMoneda, limpiarMoneda } from '../utils/formatMoney';
import { hoyISO } from '../utils/fechaHoy';
import { getUsuarioActual, CLAVE_FILTROS_PROSPECTACION } from '../apiAuth';
import { DropdownCheckbox } from './ReportesCrmHome';

// Módulo Prospectación (rol Usuario, por flag acceso_prospectacion). Lista +
// alta/edición en un solo componente. El backend (/api/prospectacion) solo
// devuelve y modifica los registros del usuario autenticado. Es independiente
// de Captura de Prospectos: "Pasado a Integración" es solo un status.
//
// Datos personales: nada de console.log con datos de prospectos.
const API = '/api/prospectacion';
const TAMANO_PAGINA = 25;
const VACIO = '—';
const MAX_OBS_DEFAULT = 10;

const fechaDMA = (iso) => (iso ? iso.slice(0, 10).split('-').reverse().join('/') : VACIO);
const toggle = (arr, setArr, v) => setArr(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);

const COLOR_STATUS = {
  'Por Contactar': { bg: '#eef2f7', fg: '#374151', bd: '#d1d5db' },
  'Contactado': { bg: '#e0ecff', fg: '#1a3a6e', bd: '#bcd3f5' },
  'En Espera de Documentación': { bg: '#fff7e6', fg: '#7a4a05', bd: '#f5d9a8' },
  'Desistimiento': { bg: '#fde2e2', fg: '#991b1b', bd: '#f5b5b5' },
  'Pasado a Integración': { bg: '#dcfce7', fg: '#166534', bd: '#a7e3bd' },
};

function StatusPill({ status }) {
  const c = COLOR_STATUS[status] || COLOR_STATUS['Por Contactar'];
  return (
    <span style={{ display: 'inline-block', fontSize: 11.5, fontWeight: 'bold', color: c.fg, backgroundColor: c.bg, border: `1px solid ${c.bd}`, borderRadius: 999, padding: '2px 10px', whiteSpace: 'nowrap' }}>
      {status}
    </span>
  );
}

// Filtros recordados por pestaña del navegador y por usuario; apiAuth.js los
// borra al cerrar sesión, al 401 y al cierre por inactividad.
const claveFiltros = () => `${CLAVE_FILTROS_PROSPECTACION}${getUsuarioActual()?.id ?? 'anon'}`;
function leerFiltros() {
  try {
    const f = JSON.parse(sessionStorage.getItem(claveFiltros()) || 'null');
    if (!f || typeof f !== 'object') return null;
    return {
      status: Array.isArray(f.status) ? f.status.map(String) : [],
      busqueda: typeof f.busqueda === 'string' ? f.busqueda : '',
      pagina: Number.isInteger(f.pagina) && f.pagina > 0 ? f.pagina : 1,
    };
  } catch { return null; }
}
function guardarFiltros(f) {
  try { sessionStorage.setItem(claveFiltros(), JSON.stringify(f)); } catch { /* sin sessionStorage: solo no se recuerdan */ }
}

async function leerJson(r) {
  try { return await r.json(); } catch { return {}; }
}

const estilos = (isMobile) => ({
  container: { padding: isMobile ? 12 : 24 },
  frame: { backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  frameHeader: {
    backgroundColor: '#1a3a6e', color: 'white', padding: isMobile ? '14px 16px' : '16px 22px',
    display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap',
    position: 'sticky', top: 'var(--app-header-h, 120px)', zIndex: 31, borderRadius: '12px 12px 0 0',
  },
  title: { margin: 0, fontSize: 17, fontWeight: 'bold', flex: 1, minWidth: 160 },
  backBtn: { background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.28)', color: 'white', fontSize: 12.5, fontWeight: 'bold', borderRadius: 7, padding: '7px 12px', cursor: 'pointer' },
  notifBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.35)', color: 'white', fontSize: 13, fontWeight: 'bold', borderRadius: 7, padding: '8px 14px', cursor: 'pointer' },
  notifBadge: { backgroundColor: '#dc2626', color: 'white', borderRadius: 999, fontSize: 11, fontWeight: 'bold', padding: '1px 7px', lineHeight: '16px' },
  nuevoBtn: { backgroundColor: '#16a34a', border: 'none', color: 'white', fontSize: 13, fontWeight: 'bold', borderRadius: 7, padding: '8px 14px', cursor: 'pointer' },
  panel: { padding: isMobile ? 14 : '20px 22px 26px' },
  filters: { display: 'flex', gap: isMobile ? 12 : 18, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 11, fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4 },
  input: { border: '1px solid #d1d5db', borderRadius: 6, padding: '7px 10px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%' },
  inputError: { borderColor: '#dc2626', backgroundColor: '#fef2f2' },
  limpiarBtn: { border: '1px solid #d1d5db', backgroundColor: 'white', color: '#1a3a6e', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 'bold', cursor: 'pointer' },
  abrirBtn: { border: 'none', backgroundColor: '#1a3a6e', color: 'white', borderRadius: 7, padding: '8px 14px', fontSize: 13, fontWeight: 'bold', cursor: 'pointer' },
  tableScroll: { border: '1px solid #e5e7eb', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', fontSize: 10.5, fontWeight: 'bold', color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3, backgroundColor: '#f9fafb', padding: '8px 10px', whiteSpace: 'nowrap', borderBottom: '1px solid #e5e7eb' },
  td: { padding: '8px 10px', borderBottom: '1px solid #e5e7eb', fontSize: 13, verticalAlign: 'middle' },
  obsCelda: { maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#374151' },
  obsFecha: { fontSize: 11, color: '#6b7280', marginRight: 6 },
  eliminarBtn: { border: '1px solid #dc2626', backgroundColor: 'white', color: '#b91c1c', borderRadius: 6, padding: '5px 10px', fontSize: 12.5, fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' },
  verBtn: { border: '1px solid #1a3a6e', backgroundColor: 'white', color: '#1a3a6e', borderRadius: 6, padding: '5px 10px', fontSize: 12.5, fontWeight: 'bold', cursor: 'pointer', whiteSpace: 'nowrap' },
  empty: { padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 },
  card: { border: '1px solid #e5e7eb', borderRadius: 10, padding: 12, marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 },
  paginacion: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginTop: 12, fontSize: 12.5, color: '#4b5563' },
  pagBtn: (disabled) => ({ border: '1px solid #d1d5db', backgroundColor: disabled ? '#f3f4f6' : 'white', color: disabled ? '#9ca3af' : '#1a3a6e', borderRadius: 7, padding: '6px 12px', fontSize: 12.5, fontWeight: 'bold', cursor: disabled ? 'default' : 'pointer' }),
  error: { backgroundColor: '#fef2f2', border: '1px solid #fecaca', color: '#991b1b', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  exito: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 },
  aviso: { backgroundColor: '#fff7e6', border: '1px solid #f5d9a8', color: '#7a4a05', borderRadius: 6, padding: '6px 10px', fontSize: 12 },
  errorCampo: { color: '#b91c1c', fontSize: 12 },
  seccion: { borderTop: '1px solid #e5e7eb', paddingTop: 16, marginTop: 16 },
  seccionTitulo: { margin: '0 0 12px', fontSize: 14, fontWeight: 'bold', color: '#1a3a6e' },
  // En escritorio los campos llevan ancho fijo (ver ANCHO) y se alinean a la izquierda.
  grid: isMobile ? { display: 'grid', gridTemplateColumns: '1fr', gap: 14 } : { display: 'flex', flexWrap: 'wrap', gap: 14 },
  montoBox: { display: 'flex', alignItems: 'center', border: '1px solid #d1d5db', borderRadius: 6, overflow: 'hidden', backgroundColor: 'white' },
  montoSigno: { padding: '7px 8px', backgroundColor: '#f3f4f6', color: '#6b7280', fontSize: 13, borderRight: '1px solid #d1d5db' },
  obsFila: { display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '160px 1fr', gap: 10, alignItems: 'start', padding: '10px 0', borderBottom: '1px dashed #e5e7eb' },
  obsNueva: { fontSize: 11, fontWeight: 'bold', color: '#16a34a', textTransform: 'uppercase', letterSpacing: 0.3 },
  limite: { backgroundColor: '#f0f4fb', border: '1px solid #d6e0f0', color: '#1a3a6e', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 'bold', marginBottom: 8 },
  acciones: { display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap', marginTop: 20 },
  guardarBtn: (disabled) => ({ backgroundColor: disabled ? '#93c5aa' : '#16a34a', border: 'none', color: 'white', fontSize: 14, fontWeight: 'bold', borderRadius: 8, padding: '10px 20px', cursor: disabled ? 'default' : 'pointer' }),
  cancelarBtn: { border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', borderRadius: 8, padding: '10px 18px', fontSize: 14, fontWeight: 'bold', cursor: 'pointer' },
});

// ==================== LISTA ====================
function ListaProspectacion({ opciones, onAbrir, onNuevo, onNotificaciones }) {
  const { isMobile } = useResponsive();
  const st = estilos(isMobile);
  const inicial = useMemo(() => leerFiltros() || { status: [], busqueda: '', pagina: 1 }, []);
  const [statusSel, setStatusSel] = useState(inicial.status);
  const [busqueda, setBusqueda] = useState(inicial.busqueda);
  const [pagina, setPagina] = useState(inicial.pagina);
  const [datos, setDatos] = useState(null);
  const [consultada, setConsultada] = useState(null); // búsqueda a la que corresponden los datos
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recarga, setRecarga] = useState(0);
  const [eliminando, setEliminando] = useState(null);

  const [porAtender, setPorAtender] = useState(0); // notificaciones pendientes con fecha hoy o anterior

  useEffect(() => { guardarFiltros({ status: statusSel, busqueda, pagina }); }, [statusSel, busqueda, pagina]);

  useEffect(() => {
    let vigente = true;
    fetch(`${API}/notificaciones`)
      .then(async r => (r.ok ? leerJson(r) : null))
      .then(d => { if (vigente && d && Array.isArray(d.filas)) setPorAtender(d.filas.filter(n => n.fecha_notificar <= d.hoy).length); })
      .catch(() => { /* el contador es solo informativo */ });
    return () => { vigente = false; };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    statusSel.forEach(s => params.append('status', s));
    if (busqueda.trim()) params.set('q', busqueda.trim());
    params.set('pagina', String(pagina));
    params.set('tamano', String(TAMANO_PAGINA));
    let vigente = true;
    // Espera de 300 ms para no consultar en cada tecla del buscador.
    const t = setTimeout(() => {
      setLoading(true);
      fetch(`${API}?${params.toString()}`)
        .then(async r => ({ ok: r.ok, d: await leerJson(r) }))
        .then(({ ok, d }) => {
          if (!vigente) return;
          if (!ok) { setError(d.error || 'No se pudo cargar la lista. Intente de nuevo.'); setDatos(null); return; }
          // Si la página guardada ya no existe (p. ej. menos registros), regresa a la última.
          if (d.total > 0 && d.filas.length === 0 && pagina > d.total_paginas) { setPagina(d.total_paginas); return; }
          setError(''); setDatos(d); setConsultada(busqueda.trim());
        })
        .catch(() => { if (vigente) setError('No se pudo cargar la lista. Revise su conexión e intente de nuevo.'); })
        .finally(() => { if (vigente) setLoading(false); });
    }, 300);
    return () => { vigente = false; clearTimeout(t); };
  }, [statusSel, busqueda, pagina, recarga]);

  const eliminar = async (f) => {
    if (!window.confirm(`¿Eliminar el prospecto "${f.nombre}" y sus observaciones? Esta acción no se puede deshacer.`)) return;
    setEliminando(f.id);
    try {
      const r = await fetch(`${API}/${f.id}`, { method: 'DELETE' });
      const d = await leerJson(r);
      if (!r.ok) { setError(d.error || `No se pudo eliminar (error ${r.status}).`); return; }
      setError('');
      setRecarga(n => n + 1);
    } catch {
      setError('No se pudo eliminar. Revise su conexión e intente de nuevo.');
    } finally {
      setEliminando(null);
    }
  };
  const botonEliminar = (f) => (
    <button style={{ ...st.eliminarBtn, opacity: eliminando === f.id ? 0.6 : 1 }} disabled={eliminando !== null} onClick={() => eliminar(f)}>
      {eliminando === f.id ? 'Eliminando…' : 'Eliminar'}
    </button>
  );

  const cambiarStatus = (v) => { toggle(statusSel, setStatusSel, v); setPagina(1); };
  const cambiarBusqueda = (v) => { setBusqueda(v); setPagina(1); };
  const hayFiltros = statusSel.length > 0 || busqueda.trim() !== '';
  const limpiar = () => { setStatusSel([]); setBusqueda(''); setPagina(1); };

  const filas = datos?.filas || [];
  // "Abrir" directo: solo si los datos mostrados corresponden a lo escrito.
  const unico = busqueda.trim() && consultada === busqueda.trim() && datos?.total === 1 && filas.length === 1 ? filas[0] : null;
  const totalPaginas = datos?.total_paginas || 1;
  const primera = datos && datos.total ? (datos.pagina - 1) * datos.tamano + 1 : 0;
  const ultima = datos ? Math.min(datos.pagina * datos.tamano, datos.total) : 0;

  const mensajeVacio = hayFiltros
    ? 'No hay prospectos con estos filtros.'
    : 'Aún no tiene prospectos. Use “+ Nuevo Prospecto” para capturar el primero.';

  return (
    <div style={st.container}>
      <div style={st.frame}>
        <div style={st.frameHeader}>
          <h2 style={st.title}>🎯 Prospectación</h2>
          <button style={st.notifBtn} onClick={onNotificaciones}>
            🔔 Notificaciones
            {porAtender > 0 && <span style={st.notifBadge} title="Pendientes con fecha de hoy o anterior">{porAtender}</span>}
          </button>
          <button style={st.nuevoBtn} onClick={onNuevo}>+ Nuevo Prospecto</button>
        </div>

        <div style={st.panel}>
          <div style={st.filters}>
            <div style={{ ...st.field, flex: isMobile ? '1 1 100%' : '0 1 320px' }}>
              <label style={st.label} htmlFor="prosp-buscar">Buscar por nombre</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input id="prosp-buscar" type="search" style={st.input} placeholder="Nombre del cliente…" value={busqueda} maxLength={100}
                  onChange={e => cambiarBusqueda(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && unico) onAbrir(unico.id); }} />
                {unico && <button style={st.abrirBtn} onClick={() => onAbrir(unico.id)} title="Abrir el único resultado (Enter)">Abrir</button>}
              </div>
            </div>
            <DropdownCheckbox label="Status" opciones={opciones.status.map(s => ({ v: s, l: s }))} seleccion={statusSel}
              onToggle={cambiarStatus} onClear={() => { setStatusSel([]); setPagina(1); }} ancho={isMobile ? 240 : 250} />
            <button style={{ ...st.limpiarBtn, opacity: hayFiltros ? 1 : 0.55 }} onClick={limpiar} disabled={!hayFiltros}>✕ Limpiar filtros</button>
          </div>

          {error && <div style={st.error}>{error}</div>}

          {isMobile ? (
            <div>
              {loading && !datos && <div style={st.empty}>Cargando...</div>}
              {datos && filas.length === 0 && <div style={st.empty}>{mensajeVacio}</div>}
              {filas.map(f => (
                <div key={f.id} style={st.card}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                    <strong style={{ fontSize: 14, color: '#111827' }}>{f.nombre}</strong>
                    <StatusPill status={f.status} />
                  </div>
                  <div style={{ fontSize: 12, color: '#4b5563' }}>Primer contacto: {fechaDMA(f.fecha_primer_contacto)}</div>
                  {f.ultima_observacion && (
                    <div style={{ fontSize: 12.5, color: '#374151' }}>
                      <span style={st.obsFecha}>{fechaDMA(f.ultima_observacion_fecha)}</span>
                      {f.ultima_observacion.length > 140 ? `${f.ultima_observacion.slice(0, 140)}…` : f.ultima_observacion}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}><button style={st.verBtn} onClick={() => onAbrir(f.id)}>Ver o Editar</button>{botonEliminar(f)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={st.tableScroll}>
              <table style={st.table}>
                <thead>
                  <tr>
                    <th style={{ ...st.th, borderTopLeftRadius: 10 }}>Nombre del cliente</th>
                    <th style={st.th}>Status</th>
                    <th style={st.th}>Fecha de primer contacto</th>
                    <th style={st.th}>Última observación</th>
                    <th style={{ ...st.th, borderTopRightRadius: 10 }} aria-label="Acciones" />
                  </tr>
                </thead>
                <tbody>
                  {loading && !datos && <tr><td colSpan={5} style={st.empty}>Cargando...</td></tr>}
                  {datos && filas.length === 0 && <tr><td colSpan={5} style={st.empty}>{mensajeVacio}</td></tr>}
                  {filas.map(f => (
                    <tr key={f.id}>
                      <td style={{ ...st.td, fontWeight: 600, color: '#111827' }}>{f.nombre}</td>
                      <td style={st.td}><StatusPill status={f.status} /></td>
                      <td style={{ ...st.td, whiteSpace: 'nowrap' }}>{fechaDMA(f.fecha_primer_contacto)}</td>
                      <td style={st.td}>
                        <div style={st.obsCelda} title={f.ultima_observacion || ''}>
                          {f.ultima_observacion
                            ? <><span style={st.obsFecha}>{fechaDMA(f.ultima_observacion_fecha)}</span>{f.ultima_observacion}</>
                            : <span style={{ color: '#9ca3af' }}>{VACIO}</span>}
                        </div>
                      </td>
                      <td style={{ ...st.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'inline-flex', gap: 8 }}><button style={st.verBtn} onClick={() => onAbrir(f.id)}>Ver o Editar</button>{botonEliminar(f)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {datos && datos.total > 0 && (
            <div style={st.paginacion}>
              <span>Mostrando {primera}–{ultima} de {datos.total}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button style={st.pagBtn(pagina <= 1)} disabled={pagina <= 1} onClick={() => setPagina(p => p - 1)}>← Anterior</button>
                <span>Página {pagina} de {totalPaginas}</span>
                <button style={st.pagBtn(pagina >= totalPaginas)} disabled={pagina >= totalPaginas} onClick={() => setPagina(p => p + 1)}>Siguiente →</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ==================== ALTA / EDICIÓN ====================
const FORM_VACIO = {
  nombre: '', telefono: '', email: '', estado_civil: '', fecha_primer_contacto: '', tipo_credito: '', tipo_inmueble: '',
  status: '', valor_inmueble: '', valor_credito: '', fecha_ultimo_contacto: '',
};
// Anchos de escritorio (px). Los "medio" son la mitad de como se veían en 1440 px.
const ANCHO = { nombre: 420, medio: 210, credito: 160 };

const renglonNuevo = () => ({ clave: `n${Math.random().toString(36).slice(2)}`, fecha: hoyISO(), texto: '' });

function aFormulario(d, statusDefault) {
  const f = { ...FORM_VACIO };
  Object.keys(FORM_VACIO).forEach(k => { f[k] = d[k] === null || d[k] === undefined ? '' : String(d[k]); });
  if (!f.status) f.status = statusDefault;
  return f;
}

function EdicionProspectacion({ id, opciones, avisosIniciales, onVolver, onCreado }) {
  const { isMobile } = useResponsive();
  const st = estilos(isMobile);
  const maxObs = opciones.max_observaciones || MAX_OBS_DEFAULT;
  // Alta nueva: fecha de primer contacto prellenada con hoy (CDMX), editable. La edición la toma de la BD tal cual.
  const fechaAlta = useRef(id ? '' : hoyISO());
  const [form, setForm] = useState({ ...FORM_VACIO, status: opciones.status_default, fecha_primer_contacto: fechaAlta.current });
  const [existentes, setExistentes] = useState([]); // [{id, numero, fecha, texto}] fecha DESC, numero DESC
  const [nuevas, setNuevas] = useState([renglonNuevo()]);
  const [cargando, setCargando] = useState(!!id);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [errores, setErrores] = useState({});
  const [avisos, setAvisos] = useState({});
  const [exito, setExito] = useState('');
  const original = useRef('');

  const instantanea = (f, ex, nu) => JSON.stringify({ f, ex: ex.map(o => [o.id, o.fecha, o.texto]), nu: nu.filter(o => o.texto.trim()).map(o => [o.fecha, o.texto]) });

  const cargar = useCallback(async (mantenerMensajes = false) => {
    if (!id) { original.current = instantanea({ ...FORM_VACIO, status: opciones.status_default, fecha_primer_contacto: fechaAlta.current }, [], []); return; }
    setCargando(true);
    try {
      const r = await fetch(`${API}/${id}`);
      const d = await leerJson(r);
      if (!r.ok) { setError(d.error || 'No se pudo cargar el prospecto.'); return; }
      const f = aFormulario(d, opciones.status_default);
      const ex = (d.observaciones || []).map(o => ({ id: o.id, numero: o.numero, fecha: o.fecha, texto: o.texto }));
      setForm(f);
      setExistentes(ex);
      setNuevas(ex.length < maxObs ? [renglonNuevo()] : []);
      original.current = instantanea(f, ex, []);
      if (!mantenerMensajes) {
        setError(''); setErrores({});
        setAvisos(avisosIniciales || {});
        setExito(avisosIniciales ? 'Prospecto guardado.' : '');
      }
    } catch {
      setError('No se pudo cargar el prospecto. Revise su conexión e intente de nuevo.');
    } finally {
      setCargando(false);
    }
  }, [id, opciones.status_default, maxObs, avisosIniciales]);

  useEffect(() => { cargar(); }, [cargar]);

  const set = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }));
    if (errores[campo]) setErrores(e => { const c = { ...e }; delete c[campo]; return c; });
  };

  const llenasNuevas = nuevas.filter(o => o.texto.trim()).length;
  const totalObs = existentes.length + llenasNuevas;
  const limiteAlcanzado = totalObs >= maxObs;

  // Se abre un renglón nuevo cuando el último se empieza a llenar, hasta el tope.
  const cambiarNueva = (clave, campo, valor) => {
    setNuevas(prev => {
      let lista = prev.map(o => (o.clave === clave ? { ...o, [campo]: valor } : o));
      const llenas = lista.filter(o => o.texto.trim()).length;
      const ultimaLlena = lista.length && lista[lista.length - 1].texto.trim();
      if (ultimaLlena && existentes.length + llenas < maxObs) lista = [...lista, renglonNuevo()];
      // Si se vacía, no deja más de un renglón vacío al final.
      while (lista.length > 1 && !lista[lista.length - 1].texto.trim() && !lista[lista.length - 2].texto.trim()) lista = lista.slice(0, -1);
      return lista;
    });
    if (errores.observaciones) setErrores(e => { const c = { ...e }; delete c.observaciones; return c; });
  };
  const cambiarExistente = (oid, campo, valor) => {
    setExistentes(prev => prev.map(o => (o.id === oid ? { ...o, [campo]: valor } : o)));
    if (errores.observaciones) setErrores(e => { const c = { ...e }; delete c.observaciones; return c; });
  };

  const sucio = !cargando && instantanea(form, existentes, nuevas) !== original.current;

  const regresar = () => {
    if (sucio && !window.confirm('Hay cambios sin guardar. ¿Regresar a la lista sin guardarlos?')) return;
    onVolver();
  };

  const guardar = async () => {
    setGuardando(true); setError(''); setExito('');
    const body = {
      ...form,
      observaciones: [
        ...existentes.map(o => ({ id: o.id, fecha: o.fecha, texto: o.texto })),
        ...nuevas.filter(o => o.texto.trim()).map(o => ({ fecha: o.fecha, texto: o.texto })),
      ],
    };
    try {
      const r = await fetch(id ? `${API}/${id}` : API, { method: id ? 'PUT' : 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const d = await leerJson(r);
      if (!r.ok) {
        setError(d.error || `No se pudo guardar (error ${r.status}).`);
        setErrores(d.errores || {});
        setAvisos({});
        return;
      }
      setErrores({});
      setAvisos(d.avisos || {});
      setExito('Prospecto guardado.');
      if (!id) onCreado(d.id, d.avisos || {});
      else await cargar(true);
    } catch {
      setError('No se pudo guardar. Revise su conexión e intente de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const anchoCampo = (w) => (isMobile || !w ? st.field : { ...st.field, flex: `0 0 ${w}px`, minWidth: 0 });
  const campoTexto = (campo, etiqueta, props = {}, w) => (
    <div style={anchoCampo(w)}>
      <label style={st.label} htmlFor={`prosp-${campo}`}>{etiqueta}</label>
      <input id={`prosp-${campo}`} style={{ ...st.input, ...(errores[campo] ? st.inputError : {}) }} value={form[campo]}
        onChange={e => set(campo, e.target.value)} aria-invalid={!!errores[campo]} {...props} />
      {errores[campo] && <span style={st.errorCampo}>{errores[campo]}</span>}
      {avisos[campo] && !errores[campo] && <span style={st.aviso}>⚠ {avisos[campo]}</span>}
    </div>
  );
  const campoSelect = (campo, etiqueta, lista, conVacio = true, w) => (
    <div style={anchoCampo(w)}>
      <label style={st.label} htmlFor={`prosp-${campo}`}>{etiqueta}</label>
      <select id={`prosp-${campo}`} style={{ ...st.input, ...(errores[campo] ? st.inputError : {}) }} value={form[campo]} onChange={e => set(campo, e.target.value)}>
        {conVacio && <option value="">-- Seleccionar --</option>}
        {lista.map(v => <option key={v} value={v}>{v}</option>)}
      </select>
      {errores[campo] && <span style={st.errorCampo}>{errores[campo]}</span>}
    </div>
  );
  const campoMonto = (campo, etiqueta, w) => (
    <div style={anchoCampo(w)}>
      <label style={st.label} htmlFor={`prosp-${campo}`}>{etiqueta}</label>
      <div style={{ ...st.montoBox, ...(errores[campo] ? st.inputError : {}) }}>
        <span style={st.montoSigno}>$</span>
        <input id={`prosp-${campo}`} inputMode="numeric" style={{ ...st.input, border: 'none', borderRadius: 0 }} value={formatearMoneda(form[campo])}
          onChange={e => set(campo, limpiarMoneda(e.target.value).slice(0, 14))} placeholder="0" />
      </div>
      {errores[campo] && <span style={st.errorCampo}>{errores[campo]}</span>}
    </div>
  );

  return (
    <div style={st.container}>
      <div style={st.frame}>
        <div style={st.frameHeader}>
          <button style={st.backBtn} onClick={regresar}>← Lista de Prospectación</button>
          <h2 style={st.title}>{id ? 'Ver o Editar Prospecto' : 'Nuevo Prospecto'}</h2>
        </div>

        <div style={st.panel}>
          {cargando ? <div style={st.empty}>Cargando...</div> : (
            <>
              {error && <div style={st.error} role="alert">{error}</div>}
              {exito && !error && <div style={st.exito}>✓ {exito}</div>}

              <h3 style={st.seccionTitulo}>Datos del cliente</h3>
              <div style={st.grid}>
                {campoTexto('nombre', 'Nombre *', { maxLength: 200, placeholder: 'Nombre y, si se quiere, apellido' }, ANCHO.nombre)}
                {campoTexto('telefono', 'Teléfono', { maxLength: 25, inputMode: 'tel', placeholder: '10 dígitos' }, ANCHO.medio)}
                {campoTexto('email', 'Email', { maxLength: 150, type: 'email', placeholder: 'correo@ejemplo.com' }, ANCHO.medio)}
                {campoSelect('estado_civil', 'Estado civil', opciones.estados_civiles || [], true, ANCHO.medio)}
              </div>

              <div style={st.seccion}>
                <h3 style={st.seccionTitulo}>Seguimiento</h3>
                <div style={st.grid}>
                  {campoSelect('status', 'Status', opciones.status, false, ANCHO.medio)}
                  {campoTexto('fecha_primer_contacto', 'Fecha de primer contacto', { type: 'date' }, ANCHO.medio)}
                  {/* La pone el servidor: fecha del alta o del último guardado con cambios. */}
                  <div style={anchoCampo(ANCHO.medio)}>
                    <label style={st.label} htmlFor="prosp-fecha_ultimo_contacto">Fecha de último contacto</label>
                    <input id="prosp-fecha_ultimo_contacto" style={{ ...st.input, backgroundColor: '#f3f4f6', color: '#374151' }} readOnly
                      value={form.fecha_ultimo_contacto ? fechaDMA(form.fecha_ultimo_contacto) : 'Se pondrá al guardar'} />
                    <span style={{ fontSize: 11, color: '#6b7280' }}>Automática: fecha del último guardado.</span>
                  </div>
                </div>
              </div>

              <div style={st.seccion}>
                <h3 style={st.seccionTitulo}>Crédito e inmueble</h3>
                <div style={st.grid}>
                  {campoSelect('tipo_credito', 'Tipo de crédito', opciones.tipos_credito, true, ANCHO.credito)}
                  {campoSelect('tipo_inmueble', 'Tipo de inmueble', opciones.tipos_inmueble, true, ANCHO.credito)}
                  {campoMonto('valor_inmueble', 'Valor del inmueble', ANCHO.credito)}
                  {campoMonto('valor_credito', 'Valor de Crédito', ANCHO.credito)}
                </div>
              </div>

              <div style={st.seccion}>
                <h3 style={st.seccionTitulo}>Observaciones ({totalObs} de {maxObs})</h3>
                {limiteAlcanzado && <div style={st.limite}>Límite de {maxObs} observaciones alcanzado. Ya no se pueden agregar más (las existentes se pueden editar).</div>}
                {errores.observaciones && <div style={{ ...st.error, marginBottom: 8 }}>{errores.observaciones}</div>}
                {nuevas.map(o => (
                  <div key={o.clave} style={st.obsFila}>
                    <div style={st.field}>
                      <span style={st.obsNueva}>Nueva observación</span>
                      <input type="date" style={st.input} value={o.fecha} onChange={e => cambiarNueva(o.clave, 'fecha', e.target.value)} aria-label="Fecha de la nueva observación" />
                    </div>
                    <textarea style={{ ...st.input, minHeight: 60, resize: 'vertical' }} maxLength={2000} placeholder="Escriba la observación…"
                      value={o.texto} onChange={e => cambiarNueva(o.clave, 'texto', e.target.value)} aria-label="Texto de la nueva observación" />
                  </div>
                ))}
                {existentes.map(o => (
                  <div key={o.id} style={st.obsFila}>
                    <input type="date" style={st.input} value={o.fecha} onChange={e => cambiarExistente(o.id, 'fecha', e.target.value)} aria-label="Fecha de la observación" />
                    <textarea style={{ ...st.input, minHeight: 60, resize: 'vertical' }} maxLength={2000}
                      value={o.texto} onChange={e => cambiarExistente(o.id, 'texto', e.target.value)} aria-label="Texto de la observación" />
                  </div>
                ))}
                {existentes.length === 0 && nuevas.every(o => !o.texto.trim()) && (
                  <div style={{ fontSize: 12.5, color: '#6b7280', paddingTop: 8 }}>Sin observaciones todavía.</div>
                )}
              </div>

              <div style={st.acciones}>
                <button style={st.cancelarBtn} onClick={regresar}>Regresar</button>
                <button style={st.guardarBtn(guardando)} disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar'}</button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}


// ==================== NOTIFICACIONES ====================
// Recordatorios con fecha (p. ej. llamar a un cliente para informarle algo).
// Se listan de la fecha más cercana a la más lejana; "Marcar como leído" las
// quita de la lista para siempre (el backend solo pone leida_en).
const NOTIF_VACIA = { fecha_notificar: '', prospectacion_id: '', comentario: '' };

function etiquetaFecha(fecha, hoy) {
  if (!hoy) return null;
  if (fecha < hoy) return { texto: 'Vencida', bg: '#fde2e2', fg: '#991b1b', bd: '#f5b5b5' };
  if (fecha === hoy) return { texto: 'Hoy', bg: '#fff7e6', fg: '#7a4a05', bd: '#f5d9a8' };
  return null;
}

function NotificacionesProspectacion({ onVolver }) {
  const { isMobile } = useResponsive();
  const st = estilos(isMobile);
  const [form, setForm] = useState(NOTIF_VACIA);
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const [exito, setExito] = useState('');
  const [error, setError] = useState('');
  const [datos, setDatos] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [marcando, setMarcando] = useState(null);
  const [capturando, setCapturando] = useState(false); // pantalla de captura vs. lista de pendientes

  const cargar = useCallback(async () => {
    try {
      const r = await fetch(`${API}/notificaciones`);
      const d = await leerJson(r);
      if (!r.ok) { setError(d.error || 'No se pudieron cargar las notificaciones.'); return; }
      setDatos(d);
    } catch {
      setError('No se pudieron cargar las notificaciones. Revise su conexión e intente de nuevo.');
    }
  }, []);

  useEffect(() => {
    cargar();
    fetch(`${API}/notificaciones/clientes`)
      .then(async r => (r.ok ? leerJson(r) : null))
      .then(d => { if (d && Array.isArray(d.filas)) setClientes(d.filas); })
      .catch(() => { /* sin lista: el cliente es opcional */ });
  }, [cargar]);

  const set = (campo, valor) => {
    setForm(f => ({ ...f, [campo]: valor }));
    if (errores[campo]) setErrores(e => { const c = { ...e }; delete c[campo]; return c; });
  };

  const sucio = form.fecha_notificar !== '' || form.comentario.trim() !== '' || form.prospectacion_id !== '';
  const abrirCaptura = () => { setForm(NOTIF_VACIA); setErrores({}); setError(''); setExito(''); setCapturando(true); };
  const cerrarCaptura = () => {
    if (sucio && !window.confirm('Hay una notificación sin guardar. ¿Regresar sin guardarla?')) return;
    setForm(NOTIF_VACIA); setErrores({}); setError(''); setCapturando(false);
  };

  const guardar = async () => {
    setError(''); setExito('');
    const errs = {};
    if (!form.fecha_notificar) errs.fecha_notificar = 'Capture la fecha a notificar.';
    if (!form.comentario.trim()) errs.comentario = 'Capture el comentario de notificación.';
    if (Object.keys(errs).length) { setErrores(errs); return; }
    setGuardando(true);
    try {
      const r = await fetch(`${API}/notificaciones`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      const d = await leerJson(r);
      if (!r.ok) { setError(d.error || `No se pudo guardar (error ${r.status}).`); setErrores(d.errores || {}); return; }
      setForm(NOTIF_VACIA); setErrores({});
      setExito('Notificación guardada.');
      setCapturando(false);
      await cargar();
    } catch {
      setError('No se pudo guardar. Revise su conexión e intente de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const marcarLeida = async (n) => {
    setMarcando(n.id); setError(''); setExito('');
    try {
      const r = await fetch(`${API}/notificaciones/${n.id}/leida`, { method: 'PUT' });
      const d = await leerJson(r);
      if (!r.ok && r.status !== 404) { setError(d.error || `No se pudo marcar como leída (error ${r.status}).`); return; }
      setDatos(prev => (prev ? { ...prev, filas: prev.filas.filter(x => x.id !== n.id) } : prev));
    } catch {
      setError('No se pudo marcar como leída. Revise su conexión e intente de nuevo.');
    } finally {
      setMarcando(null);
    }
  };

  const filas = datos?.filas || [];
  const anchoCampo = (w) => (isMobile ? st.field : { ...st.field, flex: `0 0 ${w}px`, minWidth: 0 });
  const botonLeida = (n) => (
    <button style={{ ...st.verBtn, opacity: marcando === n.id ? 0.6 : 1 }} disabled={marcando !== null} onClick={() => marcarLeida(n)}>
      {marcando === n.id ? 'Marcando…' : '✓ Marcar como leído'}
    </button>
  );
  const pildoraFecha = (n) => {
    const e = etiquetaFecha(n.fecha_notificar, datos?.hoy);
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
        <strong>{fechaDMA(n.fecha_notificar)}</strong>
        {e && <span style={{ fontSize: 11, fontWeight: 'bold', color: e.fg, backgroundColor: e.bg, border: `1px solid ${e.bd}`, borderRadius: 999, padding: '1px 8px' }}>{e.texto}</span>}
      </span>
    );
  };

  if (capturando) {
    return (
      <div style={st.container}>
        <div style={st.frame}>
          <div style={st.frameHeader}>
            <button style={st.backBtn} onClick={cerrarCaptura}>← Notificaciones</button>
            <h2 style={st.title}>Crear Notificación</h2>
          </div>
          <div style={st.panel}>
            {error && <div style={st.error} role="alert">{error}</div>}
            <div style={st.grid}>
              <div style={anchoCampo(210)}>
                <label style={st.label} htmlFor="notif-fecha">Fecha a notificar *</label>
                <input id="notif-fecha" type="date" style={{ ...st.input, ...(errores.fecha_notificar ? st.inputError : {}) }}
                  value={form.fecha_notificar} onChange={e => set('fecha_notificar', e.target.value)} aria-invalid={!!errores.fecha_notificar} />
                {errores.fecha_notificar && <span style={st.errorCampo}>{errores.fecha_notificar}</span>}
              </div>
              <div style={anchoCampo(420)}>
                <label style={st.label} htmlFor="notif-cliente">Cliente (opcional)</label>
                <select id="notif-cliente" style={{ ...st.input, ...(errores.prospectacion_id ? st.inputError : {}) }}
                  value={form.prospectacion_id} onChange={e => set('prospectacion_id', e.target.value)}>
                  <option value="">-- Sin cliente --</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                </select>
                {errores.prospectacion_id && <span style={st.errorCampo}>{errores.prospectacion_id}</span>}
              </div>
            </div>
            <div style={{ ...st.field, marginTop: 14 }}>
              <label style={st.label} htmlFor="notif-comentario">Comentario de notificación *</label>
              <textarea id="notif-comentario" style={{ ...st.input, minHeight: 70, resize: 'vertical', ...(errores.comentario ? st.inputError : {}) }}
                maxLength={1000} placeholder="Ej. Llamar al cliente para informarle el cambio de tasa…"
                value={form.comentario} onChange={e => set('comentario', e.target.value)} aria-invalid={!!errores.comentario} />
              {errores.comentario && <span style={st.errorCampo}>{errores.comentario}</span>}
            </div>
            <div style={{ ...st.acciones, marginTop: 14 }}>
              <button style={st.cancelarBtn} onClick={cerrarCaptura}>Cancelar</button>
              <button style={st.guardarBtn(guardando)} disabled={guardando} onClick={guardar}>{guardando ? 'Guardando…' : 'Guardar notificación'}</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={st.container}>
      <div style={st.frame}>
        <div style={st.frameHeader}>
          <button style={st.backBtn} onClick={onVolver}>← Lista de Prospectación</button>
          <h2 style={st.title}>🔔 Notificaciones</h2>
          <button style={st.nuevoBtn} onClick={abrirCaptura}>+ Crear Notificación</button>
        </div>

        <div style={st.panel}>
          {error && <div style={st.error} role="alert">{error}</div>}
          {exito && !error && <div style={st.exito}>✓ {exito}</div>}

          <div>
            <h3 style={st.seccionTitulo}>Pendientes ({filas.length})</h3>
            {!datos && !error && <div style={st.empty}>Cargando...</div>}
            {datos && filas.length === 0 && <div style={st.empty}>No hay notificaciones pendientes. Use “+ Crear Notificación” para capturar una.</div>}
            {filas.length > 0 && (isMobile ? (
              <div>
                {filas.map(n => (
                  <div key={n.id} style={st.card}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', fontSize: 13 }}>
                      {pildoraFecha(n)}
                    </div>
                    {n.prospecto_nombre && <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{n.prospecto_nombre}</div>}
                    <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{n.comentario}</div>
                    <div>{botonLeida(n)}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={st.tableScroll}>
                <table style={st.table}>
                  <thead>
                    <tr>
                      <th style={{ ...st.th, borderTopLeftRadius: 10, width: 170 }}>Fecha a notificar</th>
                      <th style={{ ...st.th, width: 260 }}>Cliente</th>
                      <th style={st.th}>Comentario de notificación</th>
                      <th style={{ ...st.th, borderTopRightRadius: 10 }} aria-label="Acciones" />
                    </tr>
                  </thead>
                  <tbody>
                    {filas.map(n => (
                      <tr key={n.id}>
                        <td style={st.td}>{pildoraFecha(n)}</td>
                        <td style={{ ...st.td, fontWeight: 600, color: '#111827' }}>{n.prospecto_nombre || <span style={{ color: '#9ca3af', fontWeight: 'normal' }}>{VACIO}</span>}</td>
                        <td style={{ ...st.td, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', color: '#374151' }}>{n.comentario}</td>
                        <td style={{ ...st.td, textAlign: 'right', whiteSpace: 'nowrap' }}>{botonLeida(n)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ==================== CONTENEDOR ====================
export default function Prospectacion() {
  const [opciones, setOpciones] = useState(null);
  const [errorOpciones, setErrorOpciones] = useState('');
  const [vista, setVista] = useState({ tipo: 'lista' });
  const [avisosAlta, setAvisosAlta] = useState(null);

  useEffect(() => {
    fetch(`${API}/opciones`)
      .then(async r => ({ ok: r.ok, d: await leerJson(r) }))
      .then(({ ok, d }) => (ok ? setOpciones(d) : setErrorOpciones(d.error || 'No se pudo cargar el módulo.')))
      .catch(() => setErrorOpciones('No se pudo cargar el módulo. Revise su conexión e intente de nuevo.'));
  }, []);

  if (errorOpciones) return <div style={{ padding: 24, color: '#991b1b' }}>{errorOpciones}</div>;
  if (!opciones) return <div style={{ padding: 24, color: '#6b7280' }}>Cargando...</div>;

  if (vista.tipo === 'notificaciones') {
    return <NotificacionesProspectacion onVolver={() => setVista({ tipo: 'lista' })} />;
  }
  if (vista.tipo === 'editar') {
    // Tras un alta se reabre en modo edición (ya con ids de observaciones),
    // conservando el mensaje de guardado y los avisos del servidor.
    return (
      <EdicionProspectacion key={vista.id || 'nuevo'} id={vista.id} opciones={opciones} avisosIniciales={avisosAlta}
        onVolver={() => { setAvisosAlta(null); setVista({ tipo: 'lista' }); }}
        onCreado={(nuevoId, avisos) => { setAvisosAlta(avisos); setVista({ tipo: 'editar', id: nuevoId }); }} />
    );
  }
  return <ListaProspectacion opciones={opciones} onAbrir={id => { setAvisosAlta(null); setVista({ tipo: 'editar', id }); }} onNuevo={() => { setAvisosAlta(null); setVista({ tipo: 'editar', id: null }); }}
    onNotificaciones={() => setVista({ tipo: 'notificaciones' })} />;
}
